import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from "@heroicons/react/outline";
import { HubspotAppData, HubspotAppObject, Workflow, WorkflowAutomation } from "./interface";
import { useEffect, useMemo, useState } from "react";
import { BLOCK_ICON_MAP, EMAIL_REGEX } from "./utils";
import AppHubspotContactsInput from "./HubspotContactsInput";
import Breadcrumbs from "./Breadcrumbs";
import { useTranslation } from "react-i18next";
import { postData } from "./backend";

interface Props {
  token: string;
  workflow: Workflow;
  appData: HubspotAppData | null;
  currentPage: "DATA" | "RECIPIENTS";
  onClickBack: () => void;
  onClickRecipeints: () => void;
  onStartAutomation: (automation: WorkflowAutomation) => void;
}

function AppWorkflowData(props: Props) {
  const { t } = useTranslation();

  const { token, appData } = props;

  const { workflow } = appData || props;
  const associatedObjects = props.appData?.associatedObjectOptions;

  const [filterValue, setFilterValue] = useState<string>("");

  const [selectedObjects, setSelectedObjects] = useState<Record<string, boolean>>();
  const [selectedContacts, setSelectedContacts] = useState<Record<string, string>>();

  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
  const currentDocument = workflow?.documents?.[currentDocumentIndex];

  const [hasSetAssumedOverrides, setHasSetAssumedOverrides] = useState(false);

  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<Error>();

  const contactOptions = useMemo(() => appData?.associatedObjectOptions.find((objList) => objList.toId === "0-1")?.items ?? [], [appData?.associatedObjectOptions]);

  const isRunningFromSecondary = appData?.primaryObjectType !== appData?.sourceObjectType
  const allSectedContactsValid = Object.values(selectedContacts || {}).every((email) => (email || "").match(EMAIL_REGEX));

  const hasRecipients = !!workflow.signatureConfig;

  useEffect(() => {
    if (!appData || !contactOptions || hasSetAssumedOverrides)
      return;

    const initialContacts: Record<string, string> = {};

    for (const index in appData.signatureRequestInitialRecipients) {
      const recipientNumber = parseInt(index) + 1;

      const initialValue = appData.signatureRequestInitialRecipients[index];
      initialContacts[recipientNumber] = initialValue;

      const hubspotContactTag = initialValue.match(/{{__hscontact_(.+?)(?::(.+))?}}/);
      if (!hubspotContactTag)
        continue;

      const filteredContact = contactOptions.filter((contact) => !!contact.email);
      if (filteredContact.length === 0)
        continue;

      initialContacts[recipientNumber] = filteredContact[0].email!;

      const labelDisplayName = hubspotContactTag[2];
      if (!labelDisplayName)
        continue;

      for (const contact of filteredContact) {
        const hasLabel = !!contact.associationLabels?.find((label) => label.split(":")[0] === labelDisplayName);

        if (hasLabel && contact.email) {
          initialContacts[recipientNumber] = contact.email;
          break;
        }
      }
    }

    setHasSetAssumedOverrides(true);
    setSelectedContacts(initialContacts);
  }, [appData, contactOptions, hasSetAssumedOverrides, setSelectedContacts]);

  useEffect(() => {
    if (!appData || selectedObjects !== undefined)
      return;

    setSelectedObjects(appData.associatedObjectOptions.reduce((acc: Record<string, boolean>, objList: any) => {
      objList.items.forEach((obj: any) => acc[`${objList.toId}.${obj.id}`] = true);
      return acc;
    }, {} as Record<string, boolean>));

  }, [appData, selectedObjects]);

  function handleToggleSelectedObject(id: string) {
    if (id === appData?.sourceObjectCompoundId)
      return

    setSelectedObjects((prev) => {
      if (prev === undefined)
        return { [id]: true };

      const newValue = !prev[id];
      return { ...prev, [id]: newValue };
    });
  }

  function handleToggleAll(objectType: string, checked: boolean) {
    setSelectedObjects((prev) => {
      if (prev === undefined)
        prev = {};

      for (const id of Object.keys(prev)) {
        if (id.startsWith(objectType + "."))
          prev[id] = checked;
      }

      return { ...prev };
    });
  }

  function handleRemoveOthers(objectType: string) {
    handleToggleAll(objectType, false);

    const objList = associatedObjects!.find((objList) => objList.toId === objectType)!;
    const filteredObjects = objList.items.filter(a => a.label?.toLocaleLowerCase().includes(filterValue.toLocaleLowerCase()))

    for (const obj of filteredObjects)
      handleToggleSelectedObject(`${objList.toId}.${obj.id}`);

  }

  function isAllSelected(objectType: string): boolean {
    if (!associatedObjects)
      return false;

    return !!associatedObjects
      .find((objList) => objList.toId === objectType)?.items
      .every((obj) => selectedObjects?.[`${objectType}.${obj.id}`]);
  }

  function handleSetValue(recipientNumber: number, value: string) {
    setSelectedContacts(prev => ({ ...prev, [recipientNumber]: value }));
  }

  function handleStartAutomation() {
    setIsStarting(true);
    const associatedObjectIds = (() => {
      if (selectedObjects === undefined)
        return undefined;

      return Object.entries(selectedObjects)
        .filter(([_, value]) => value)
        .map(([key, _]) => key);
    })();

    let selectedContactIds: Record<string, string> = {};
    for (const [key, email] of Object.entries(selectedContacts || {})) {
      const contact = contactOptions.find(contact => contact.email === email);
      if (contact)
        selectedContactIds[key] = contact.id;
    }

    const automationOptions: Record<string, any> = {
      "associatedObjectIds": associatedObjectIds,
      "selectedContacts": selectedContacts,
      "selectedContactIds": selectedContactIds,
      ...(isRunningFromSecondary ? {
        "mode": "ASSOCIATION_SOURCE_ID",
        "associatedSourceId": appData?.sourceObjectCompoundId,
        // source object MUST be included in the associated objects payload
        "associatedObjectIds": [...associatedObjectIds || [], appData?.sourceObjectCompoundId],
      } : {})
    }

    postData(`/hubspot/app/data/${workflow.id}/`, token, automationOptions)
      .then(data => props.onStartAutomation(data))
      .catch(setError)
      .finally(() => setIsStarting(false));
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2">
        <div className="gap-6 text-red font-semibold text-center">
          An error has occured
        </div>
        <div className="text-gray-600 text-center">
          {error.message}
        </div>
      </div>
    );
  }

  if (!appData) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2">
        <img src="https://static.portant.co/portant-loading-gray.svg" alt="Loading Logo" className='w-32 h-32' />
        <div className="text-gray-400 font-semibold text-center text-sm mt-2">{t("Loading workflow data")}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <Breadcrumbs workflow={workflow} currentPage={props.currentPage} />
      <div className="flex gap-8 h-[calc(100vh-70px)]">
        <div className="flex h-full">
          {!!currentDocument &&
            <div className="w-[600px] flex flex-col">
              <div className="flex w-full items-center mb-2">
                <div className="flex justify-center items-center my-auto mr-2 rounded w-10 h-10">
                  <img className="h-10 w-10 rounded-md" src={BLOCK_ICON_MAP[currentDocument?.documentType]} alt="Google File Icon" />
                </div>
                <div className="flex flex-col h-full justify-center">
                  <div className="flex items-center text-gray-500 text-sm font-gilroy font-semibold">
                    {t("Template Preview")}
                  </div>
                  <div className="flex text-sm font-semibold font-gilroy">
                    <div className="truncate max-w-[400px]">
                      {currentDocument?.file?.name}
                    </div>
                  </div>
                </div>
                {workflow.documents.length > 1 &&
                  <div className="flex items-center ml-auto">
                    <ChevronLeftIcon className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer" onClick={() => setCurrentDocumentIndex(i => Math.max(0, i - 1))} />
                    <div className="font-semibold text-gray-600 font-gilroy w-12 text-center">
                      {currentDocumentIndex + 1} / {workflow.documents.length}
                    </div>
                    <ChevronRightIcon className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer" onClick={() => setCurrentDocumentIndex(i => Math.min(i + 1, workflow.documents.length - 1))} />
                  </div>
                }
              </div>
              {(currentDocument.microsoftDocumentConfig?.fileEmbedViewUrl) &&
                <iframe title={t("Microsoft Editor")} className="w-full h-full border border-gray-300 rounded overflow-hidden" src={currentDocument.microsoftDocumentConfig.fileEmbedViewUrl} />
              }
              {(!currentDocument.microsoftDocumentConfig?.fileEmbedViewUrl && currentDocument?.file?.url) &&
                <iframe title={t("Google Editor")} className="w-full h-full border border-gray-300 rounded overflow-hidden" src={`${currentDocument.file?.url.replace("/edit", "/preview")}?widget&rm=minimal`} />
              }
              {(!currentDocument.microsoftDocumentConfig?.fileEmbedViewUrl && !currentDocument?.file?.url) &&
                <div className="m-auto text-sm font-semibold text-gray-400">{t("Unable to load document preview")}</div>
              }
            </div>
          }
        </div>

        <div className="flex flex-col w-[368px] h-full">

          <div className="flex flex-col">
            <div className="flex items-center gap-2 w-full">
              <ChevronLeftIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 cursor-pointer" onClick={props.onClickBack} />
              <div className="font-semibold font-gilroy">
                {workflow.name}
              </div>
            </div>
            <div className="text-xs mt-2 font-semibold text-gray-400">
              {props.currentPage === "DATA"
                ? t("Select the associated data to include in this automation")
                : t("Select the intended recipients for this signable document")
              }
            </div>
          </div>

          <div className="flex flex-col overflow-y-auto pt-4 border-t mb-4">
            {props.currentPage === "RECIPIENTS" &&
              <div className="flex flex-col gap-4 h-[calc(100vh)]">
                {Array.from({ length: appData?.signatureRequestNumRecipients || 0 }).map((_, index) =>
                  <AppHubspotContactsInput key={index} index={index} value={selectedContacts![index + 1]} setValue={handleSetValue} appData={appData} />
                )}
              </div>
            }

            {props.currentPage === "DATA" &&
              <div className="flex justify-between flex-wrap">
                {(associatedObjects?.flatMap((objLit) => objLit.items)?.length ?? 0) > 20 &&
                  <div className="relative mb-4">
                    <SearchIcon className="h-4 w-4 text-gray-400 absolute left-1 top-1.5" />
                    <input className="w-full border rounded pl-6 text-sm py-1 font-gilroy" type="text" value={filterValue} onChange={(e) => setFilterValue(e.currentTarget.value)} />
                  </div>
                }
                <div className="flex flex-col overflow-y-auto gap-4 h-full mb-4 w-full">
                  {associatedObjects?.length === 0 &&
                    <div className="text-gray-400 font-semibold text-sm w-[300px]">{t("No associated objects to select from, the automation can be started as is.")}</div>
                  }
                  {associatedObjects?.map((objList) => (
                    <div key={objList.toId} className="flex flex-col w-full">
                      <div className="flex items-center gap-2 border border-white rounded p-1 cursor-default">
                        <input type="checkbox" checked={isAllSelected(objList.toId)} disabled={(filterValue.length > 0) || appData.sourceObjectType === objList.toId} onChange={() => handleToggleAll(objList.toId, !isAllSelected(objList.toId))} />
                        <div className="text-sm text-black font-semibold font-gilroy underline">{objList.title}</div>
                        <div className="text-xs text-gray-400 font-semibold ml-auto mr-1">{Object.entries(selectedObjects ?? {}).filter(([k, v]) => v && k.startsWith(objList.toId)).length} / {objList.items.length}</div>
                      </div>
                      {objList.items.filter(a => a.label?.toLocaleLowerCase().includes(filterValue.toLocaleLowerCase())).length === 0 &&
                        <div className="text-xs font-semibold text-gray-400 text-center mt-1">
                          {t("No")} {objList.title} {t("match this search")}
                        </div>
                      }
                      <div className="flex flex-col gap-1 w-full">
                        {objList.items.filter(a => a.label?.toLocaleLowerCase().includes(filterValue.toLocaleLowerCase())).sort((a, b) => a.label?.localeCompare(b.label)).map((obj: HubspotAppObject) => (
                          <div key={obj.id} className="flex items-center gap-2 border rounded p-1 hover:bg-blue-50 cursor-pointer w-full" onClick={() => handleToggleSelectedObject(`${objList.toId}.${obj.id}`)}>
                            <input type="checkbox" checked={!!(selectedObjects?.[`${objList.toId}.${obj.id}`])} onChange={(e) => { handleToggleSelectedObject(`${objList.toId}.${obj.id}`); e.stopPropagation(); }} onClick={(e) => { e.stopPropagation(); }} disabled={`${objList.toId}.${obj.id}` === appData.sourceObjectCompoundId} />
                            <div className="flex items-center justify-between w-full">
                              <div className="text-sm text-gray-600 font-semibold font-gilroy truncate max-w-60">{obj.label}</div>
                              <div className="text-xs text-gray-400 font-semibold flex-shrink-0">{obj.email || obj.id}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {isAllSelected(objList.toId) && filterValue.length > 0 && objList.items.length > 1 && objList.items.filter(a => a.label?.toLocaleLowerCase().includes(filterValue.toLocaleLowerCase())).length > 0 &&
                        <div className="text-xs font-semibold text-gray-400 hover:text-blue cursor-pointer underline text-center mt-2" onClick={() => handleRemoveOthers(objList.toId)}>
                          {t("Unselect all others")}
                        </div>
                      }
                    </div>
                  ))}
                </div>
              </div>
            }
          </div>

          {hasRecipients && props.currentPage === "DATA" &&
            <button className="btn btn-white w-full h-10 text-sm font-semibold font-gilroy flex items-center justify-center gap-2 mt-auto" onClick={() => props.onClickRecipeints()}>
              {t("Select Recipients")}
            </button>
          }

          {(!hasRecipients || props.currentPage === "RECIPIENTS") &&
            <button className="btn btn-blue w-full h-10 text-sm font-semibold font-gilroy flex items-center justify-center gap-2 mt-auto" onClick={handleStartAutomation} disabled={!allSectedContactsValid || isStarting}>
              {isStarting ? t("Starting...") : t("Start Automation")}
            </button>
          }
        </div>
      </div>
    </div>
  );
}

export default AppWorkflowData;
